import React, { createContext, useContext, useRef, useState, useEffect } from "react";

interface MusicPlayerContextType {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  muted: boolean;
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  handleSeek: (percent: number) => void;
  handleBackward: () => void;
  handleForward: () => void;
  handleVolumeChange: (v: number) => void;
  toggleMute: () => void;
}

const MusicPlayerContext = createContext<MusicPlayerContextType | null>(null);

const VOLUME_KEY = "hero-music-volume";
const MUTED_KEY = "hero-music-muted";
const DEFAULT_VOLUME = 0.05;

const getStoredVolume = (): number => {
  try {
    const saved = localStorage.getItem(VOLUME_KEY);
    if (saved === null) return DEFAULT_VOLUME;
    const parsed = parseFloat(saved);
    return isNaN(parsed) ? DEFAULT_VOLUME : Math.min(1, Math.max(0, parsed));
  } catch {
    return DEFAULT_VOLUME;
  }
};

const getStoredMuted = (): boolean => {
  try {
    return localStorage.getItem(MUTED_KEY) === "true";
  } catch {
    return false;
  }
};

export const MusicPlayerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState<number>(getStoredVolume);
  const [muted, setMuted] = useState<boolean>(getStoredMuted);
  const [lastVolume, setLastVolume] = useState<number>(getStoredVolume);

  // Create the audio element ONCE, imperatively — not via JSX/autoPlay attribute.
  useEffect(() => {
    const audio = new Audio("/music.mp3");
    audio.loop = true;
    audio.volume = volume;   // set volume BEFORE play — kills the race entirely
    audio.muted = muted;
    audioRef.current = audio;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMeta = () => setDuration(audio.duration);
    const onEnded = () => { setIsPlaying(false); setCurrentTime(0); };

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("loadedmetadata", onLoadedMeta);
    audio.addEventListener("ended", onEnded);

    audio.play().then(() => setIsPlaying(true)).catch(() => {});

    return () => {
      // This provider should never unmount during the app's life, but just in case:
      audio.pause();
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("loadedmetadata", onLoadedMeta);
      audio.removeEventListener("ended", onEnded);
    };
  }, []); // runs exactly once for the whole app session

  const play = () => {
  const audio = audioRef.current;
  if (!audio) return;
  audio.play().then(() => setIsPlaying(true)).catch(() => {});
};

const pause = () => {
  const audio = audioRef.current;
  if (!audio) return;
  audio.pause();
  setIsPlaying(false);
};

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) audio.pause(); else audio.play();
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (percent: number) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    audio.currentTime = percent * duration;
    setCurrentTime(audio.currentTime);
  };

  const handleBackward = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, audio.currentTime - 10);
    setCurrentTime(audio.currentTime);
  };

  const handleForward = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.min(duration, audio.currentTime + 10);
    setCurrentTime(audio.currentTime);
  };

  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume);
    const isMuted = newVolume === 0;
    setMuted(isMuted);
    if (newVolume > 0) setLastVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
      audioRef.current.muted = isMuted;
    }
    try {
      localStorage.setItem(VOLUME_KEY, newVolume.toString());
      localStorage.setItem(MUTED_KEY, isMuted.toString());
    } catch {}
  };

  const toggleMute = () => {
    if (muted || volume === 0) {
      handleVolumeChange(lastVolume || DEFAULT_VOLUME);
    } else {
      setLastVolume(volume);
      handleVolumeChange(0);
    }
  };

  return (
    <MusicPlayerContext.Provider
      value={{ isPlaying, currentTime, duration, volume, muted, togglePlay, handleSeek, handleBackward, handleForward, handleVolumeChange, toggleMute, play, pause }}
    >
      {children}
    </MusicPlayerContext.Provider>
  );
};

export const usePlayer = () => {
  const ctx = useContext(MusicPlayerContext);
  if (!ctx) throw new Error("usePlayer must be used within MusicPlayerProvider");
  return ctx;
};